import { getPaymentListModel } from '../../subscriptionInfo/helpers/mySubscriptionHelpers';
import {
  getCurrencyDetails,
  formatDateString,
  createDateFormat,
  getLocalDateFormat,
} from '../../utils/common';
import CONSTANTS from '../../constants/externalReferences';
import {
  threeDSSettings,
  sbxAPIurl,
  windowCardinalConstants,
} from '../../constants/checkout.constant';
import { parseIntroPricing } from '../../utils/parseIntroPricing';
import { postMessage } from '../../utils/postMessage';
import { UPGRADE } from '../../constants/upgradeJourney.constant';

const loadScript = (url, callback) => {
  const script = document.createElement('script');
  script.type = 'text/javascript';

  script.onload = () => {
    callback();
  };

  script.src = url;
  window.document.body.appendChild(script);
};

export const getCOQVoucherCodePayload = (redemptionCode, shoppingCart) => {
  const { Currency } = shoppingCart;
  const redemptionCodes = redemptionCode
    ? [`${redemptionCode}${Currency}`]
    : [];
  return {
    RedemptionCodes: redemptionCodes,
    ShoppingCart: shoppingCart,
  };
};

export const getGenericTranslatedOrderItems = (productResponse, currency) => {
  const { Name, Id, ThumbnailUrl } = productResponse?.Product;

  const translatedOrderItemsListGeneric = [
    {
      name: Name,
      productId: Id,
      thumbnailUrl: ThumbnailUrl,
      totalAmountDisplay: getCurrencyDetails(0, currency)
        .priceWithCurrencySymbol,
    },
  ];
  return translatedOrderItemsListGeneric;
};

export const getThumbnailUrl = (Product, PricingPlan, productMetadata) => {
  let thumbnailUrl = Product?.ThumbnailUrl || '';
  if (productMetadata) {
    const fullPricingPlan = productMetadata?.PricingPlans?.find(
      (candidate) => candidate.Id === PricingPlan.Id,
    );
    if (fullPricingPlan && fullPricingPlan.AdditionalInformationURL) {
      thumbnailUrl = fullPricingPlan.AdditionalInformationURL;
    }
  }
  return thumbnailUrl;
};

const findPricingPlanMetadata = (id, productMetadata) => {
  const result = productMetadata?.PricingPlans?.find(
    (value) => value.Id === id,
  );
  return result;
};

const isFreeTrial = (pricingPlan) => {
  const proprety = pricingPlan?.AdditionalProperties?.find(
    (additionalProperty) =>
      additionalProperty?.ExternalReference === 'FreeTrial',
  );
  return proprety?.Values[0]?.toLowerCase() === 'true';
};

export const getTranslatedOrderItemsList = (
  calculateOrderQuoteResponse,
  productMetadata,
) => {
  const {
    orderQuote: { Quote },
  } = calculateOrderQuoteResponse;
  const { AppliedDiscounts, Items, Totals, Currency } = Quote;

  const hasAppliedDiscount = Boolean(AppliedDiscounts);

  const translatedOrderItemsList = Items.map((orderItem) => {
    const { Product, PricingPlan } = orderItem;
    const pricingPlanId = PricingPlan?.Id;
    const pricingPlanMetadata = findPricingPlanMetadata(
      pricingPlanId,
      productMetadata,
    );

    const thumbnailUrl = getThumbnailUrl(Product, PricingPlan, productMetadata);

    const viewOrderItem = {
      name: orderItem.Product.Name,
      thumbnailUrl: thumbnailUrl,
      totalAmountDisplay: hasAppliedDiscount
        ? getCurrencyDetails(Totals.TotalAmount, Currency)
            .priceWithCurrencySymbol
        : getCurrencyDetails(Totals.GrossAmount, Currency)
            .priceWithCurrencySymbol,
      grossAmount: getCurrencyDetails(Totals.GrossAmount, Currency).priceWithCurrencySymbol,
      productId: Product.Id,
      pricingPlanId: PricingPlan.Id,
      pricingPlanName: PricingPlan.Name,
      RenewalCycle: `subscriptionCycleType${PricingPlan.SubscriptionBillingCycle}`,
      subTotalAmountDisplay: getCurrencyDetails(Totals.SubTotalAmount, Currency)
        .priceWithCurrencySymbol,
      switchTotalAmountDisplayTemplate: hasAppliedDiscount,
      introPricing: parseIntroPricing(pricingPlanMetadata),
      isFreeTrial: isFreeTrial(pricingPlanMetadata),
    };

    return viewOrderItem;
  });

  return translatedOrderItemsList;
};

export const getOrderQuoteUtilities = {
  getPaymentInstrumentIds: (orderQuote) => {
    if (orderQuote) {
      if (orderQuote.PaymentInstruments[0]?.Id) {
        const candidates = orderQuote.PaymentInstruments.map(
          (paymentInstrument) => paymentInstrument.Id,
        );
        return candidates;
      }
    }
    return [];
  },

  getFirstPaymentInstrumentId: (orderQuote) => {
    const paymentInstrumentIds =
      getOrderQuoteUtilities.getPaymentInstrumentIds(orderQuote);
    if (paymentInstrumentIds?.length) {
      return [paymentInstrumentIds[0]];
    } else {
      return [];
    }
  },

  getCurrency: (orderQuote) => {
    const currency = orderQuote?.Quote?.Currency;
    if (currency) {
      return currency;
    }
    return '';
  },
};

export const getDefaultShoppingCart = (shoppingCartListNode) => {
  const { ShoppingCart } = shoppingCartListNode || {};
  if (!ShoppingCart) return {};
  const getDefaultOrderList = {
    ...ShoppingCart,
    Items: [
      {
        ...ShoppingCart.Items[0],
        PricingPlan: {
          Currency: ShoppingCart.Currency,
          ChargeAmount: ShoppingCart.TotalAmount,
        },
      },
    ],
  };
  return getDefaultOrderList;
};

export const getSetup3DSWeb = (callback) => {
  let threeDSMerchantDDCEnvUrl;
  if (ASCENDON_ENV_CONFIG.apiUrl === sbxAPIurl) {
    threeDSMerchantDDCEnvUrl = threeDSSettings.cyberSource.songbirdUrl.sandbox;
  } else {
    threeDSMerchantDDCEnvUrl =
      threeDSSettings.cyberSource.songbirdUrl.production;
  }
  loadScript(threeDSMerchantDDCEnvUrl, () => {
    window.Cardinal.configure(threeDSSettings.cyberSource.settings.config);
    if (callback) {
      callback();
    }
  });
};

export const getIsSessionCountrySupported = (
  externalDataCountries,
  sessionCountry,
) =>
  externalDataCountries?.countryRules?.some(
    (countryItem) => countryItem.Value === sessionCountry?.toUpperCase(),
  );

export const getPaymentMethodListNodeValue = (
  isCreatePaymentInstrument,
  updatedPaymentInstrumentNode,
  paymentMethodList,
  paymentInstrumentId,
) =>
  isCreatePaymentInstrument
    ? [updatedPaymentInstrumentNode]
    : paymentMethodList.reduce((all, item) => {
        if (item.PaymentInstrumentId === paymentInstrumentId) {
          all.push(updatedPaymentInstrumentNode);
        } else {
          all.push(item);
        }
        return all;
      }, []);

export const getUpdatedAddressList = (
  addressListNode,
  updatedPaymentInstrumentNode,
) => {
  const { BillingAddress } = updatedPaymentInstrumentNode;
  const isBillingAddressAvailable = addressListNode.some(
    (item) => item.Id === BillingAddress.Id,
  );

  const addressList = isBillingAddressAvailable
    ? addressListNode
    : [...addressListNode, { ...BillingAddress }];
  const BillingAddressNode = isBillingAddressAvailable
    ? {}
    : { BillingAddress: BillingAddress.Id };
  return { addressList, BillingAddress: BillingAddressNode };
};

export const getRetrieveDefaultPaymentUiModelNode = (
  isCalculateOrderQuote,
  getRetrieveDefaultPaymentUiModel,
  selectedPaymentInstrumentId,
) => {
  if (!isCalculateOrderQuote) {
    const paymentUiModelNode = getRetrieveDefaultPaymentUiModel.filter(
      (item) => item.Default,
    );
    return {
      paymentUiModelNode,
      changeWithoutAccountNumber: false,
    };
  }
  const paymentUiModelNode = getRetrieveDefaultPaymentUiModel.filter(
    (item) => item.PaymentInstrumentId === selectedPaymentInstrumentId,
  );
  return {
    paymentUiModelNode,
    changeWithoutAccountNumber: true,
  };
};

export const retrieveDefaultPaymentModel = (paymentInstruments) =>
  (paymentInstruments || []).reduce((all, item) => {
    const getSubscriptionNode = getPaymentListModel(
      { details: { PaymentInstrument: { ...item } } },
      item.Id,
    );
    all.push(...getSubscriptionNode);
    return all;
  }, []);

export const getPaymentInstrumentListNode = (paymentInstrumentList) =>
  (paymentInstrumentList || []).filter((item) => item.Default);

const merchantEventHandler = (
  submitOrderCountry3DSPayload,
  requestSubmitOrderUpdateCountry,
  threeDSpaymentError6170124,
  staticApiKey,
  staticApiUrl,
  setShowBackdrop,
) => {
  const { paymentsValidated } = windowCardinalConstants;

  const callbackParams = (data, jwt) => {
    console.warn(data, jwt);
    postMessage('module.hideBackdrop');
    if (setShowBackdrop) {
      setShowBackdrop(false);
    }
    document.body.classList.remove('challange-window');
    if (data) {
      const getDataErrorHandler = (error) => {
        const dataError = {
          0: () => {
            // Handle successful authentication scenario
            postMessage('module.showLoader', {});
            window.Cardinal.off('payments.validated');
            console.info(jwt);
            let requestObj;

            const submitOrderUpdateCountryPayload = {
              ...submitOrderCountry3DSPayload,
              ThreeDSDetail: {
                PaymentAuthenticationResult: jwt,
              },
            };

            requestObj = {
              submitOrderUpdateCountryPayload,
              staticApiKey,
              staticApiUrl,
            };
            requestSubmitOrderUpdateCountry(requestObj);
          },

          default: () => threeDSpaymentError6170124(),
        };

        return (dataError[error] || dataError['default'])();
      };

      getDataErrorHandler(data.ErrorNumber);
    }
  };
  window.Cardinal.off(paymentsValidated);
  window.Cardinal.on(paymentsValidated, callbackParams);
};

export const getInitialize3DSWeb = (
  jwtToken,
  retrieveDefaultPaymentResponse,
  callback,
  submitOrder3DSHandler,
  requestSubmitOrderUpdateCountry,
  threeDSpaymentError6170124,
  staticApiKey,
  staticApiUrl,
  setShowBackdrop,
) => {
  const { initialization, binProcess, paymentSetUpComplete } =
    windowCardinalConstants;

  const callbackParams = (setupCompleteData) => {
    console.info('Setup completed: ', setupCompleteData);
    const threeDSReferenceId = setupCompleteData.sessionId;
    const submitOrderUpdateCountryPayload = submitOrder3DSHandler();
    merchantEventHandler(
      submitOrderUpdateCountryPayload,
      requestSubmitOrderUpdateCountry,
      threeDSpaymentError6170124,
      staticApiKey,
      staticApiUrl,
      setShowBackdrop,
    );
    const existing = retrieveDefaultPaymentResponse.length;
    let account;
    if (existing) {
      account = retrieveDefaultPaymentResponse[0]?.Id;
    }

    window.Cardinal.trigger(binProcess, account)
      .then(function (results) {
        if (results.Status) {
          // Bin profiling was successful. Some merchants may want to only move forward with CCA if profiling was successful
          console.info('Bin profiling was successful', results);
        } else {
          // Bin profiling failed
          console.warn('Bin profiling failed', results);
        }
        if (callback) {
          callback(threeDSReferenceId);
        }

        // Bin profiling, if this is the card the end user is paying with you may start the CCA flow at this point
        // This is for cardinal standard
        //Cardinal.start('cca', myOrderObject);
      })
      .catch(function (error) {
        // An error occurred during profiling
        console.error('Bin detection: ', error);
      });
  };

  // Init songbirdJS
  window.Cardinal?.setup(initialization, { jwt: jwtToken });
  window.Cardinal?.off(paymentSetUpComplete);
  window.Cardinal?.on(paymentSetUpComplete, callbackParams);
};

export const getAdditionalPropertiesList = (additionalProperties) =>
  additionalProperties.reduce((all, item) => {
    if (item.value) {
      all.push({
        ExternalReference: item.key,
        Values: [item.value],
      });
    }
    return all;
  }, []);

export const setSubmitOrderUpdateCountryPayload = (requestPayload) => {
  const {
    redemptionCodesPayload,
    defaultPaymentResponse,
    ShoppingCart,
    country,
    leadSourceValue,
    isSecondSubmitOrder,
  } = requestPayload;

  const additionalPropertiesList = !isSecondSubmitOrder
    ? [
        { key: CONSTANTS.COUNTRY_CODE, value: country },
        { key: CONSTANTS.LEAD_SOURCE, value: leadSourceValue },
      ]
    : [{ key: CONSTANTS.COUNTRY_CODE, value: country }];

  const getAdditionalProperties = getAdditionalPropertiesList(
    additionalPropertiesList,
  );
  const RedemptionCodes = redemptionCodesPayload?.length
    ? redemptionCodesPayload
    : undefined;

  if (!isSecondSubmitOrder) {
    return {
      RedemptionCodes,
      ShippingMethodId: null,
      ShipToName: null,
      PaymentInstrumentIds: defaultPaymentResponse.paymentInstrumentIds.map(
        (paymentInstrument) => paymentInstrument.Id,
      ),
      UseDefaults: false,
      ShoppingCart,
      AdditionalProperties: getAdditionalProperties,
    };
  }
  return {
    RedemptionCodes,
    PaymentInstrumentIds: defaultPaymentResponse.paymentInstrumentIds.map(
      (paymentInstrument) => paymentInstrument.Id,
    ),
    ShoppingCart,
    AdditionalProperties: getAdditionalProperties,
  };
};

export const getSubmitOrderResponseContextData = (response, key) =>
  response?.Fault?.ContextData?.find((item) => item?.Key === key)?.Value;

export const getThreeDSChallengeHandler = (completionObj) => {
  window.Cardinal?.continue(
    'cca',
    {
      AcsUrl: completionObj.PaymentCompletionURL,
      Payload: completionObj.PaymentCompletionToken,
    },
    {
      OrderDetails: {
        TransactionId: completionObj.PaymentAuthenticationTransactionId,
      },
    },
  );
};

export const shouldWeShowAdditionalSubscriberDetails = (subscriber) => {
  const emptyValueFirstname = 'Subscriber';
  const emptyValuesLastName = ['Lastname', 'Surname'];
  const emptyBirthDate = formatDateString('1912-01-01T00:00:00Z');
  const hasFirstName =
    subscriber?.FirstName &&
    subscriber.FirstName !== '' &&
    subscriber.FirstName !== emptyValueFirstname &&
    subscriber.FirstName.length !== 1;
  const hasLastName =
    subscriber?.LastName &&
    subscriber.LastName !== '' &&
    emptyValuesLastName.indexOf(subscriber.LastName) === -1 &&
    subscriber.LastName.length !== 1;
  const subscriberBirthDate = formatDateString(subscriber.BirthDate);
  const isDefaultBirthDate = subscriberBirthDate === emptyBirthDate;
  const hasBirthDate =
    subscriber?.BirthDate && subscriber.BirthDate !== '' && !isDefaultBirthDate;

  return !(hasFirstName && hasLastName && hasBirthDate);
};

export const getUpdateSubscriberApiPayload = (formik, language) => {
  const { FirstName, LastName, DateOfBirth } = formik.values;
  const dateFormat = createDateFormat(getLocalDateFormat());
  const parsedDateformat = dateFormat.parse(DateOfBirth);
  const birthDateTimestamp =
    dateFormat.formatForSubscriberAPI(parsedDateformat);
  const Subscriber = {
    FirstName,
    LastName,
    BirthDate: birthDateTimestamp,
    Language: language,
  };
  return Subscriber;
};

export const getUpgradePlanState = (locationState) => {
  if (!locationState) {
    return null;
  }
  return locationState?.pathFrom === UPGRADE
    ? { ...locationState, isUpgradePlan: true }
    : { ...locationState, isUpgradePlan: false };
};

export const getExistingSubscriptionForUpgrade = (upgradePlanStateDetails) => {
  if (!upgradePlanStateDetails) {
    return {};
  }
  return {
    SubscriptionId: upgradePlanStateDetails?.activeSubscriptionDetails?.Id,
    SubscriptionItemId:
      upgradePlanStateDetails?.activeSubscriptionDetails?.Items?.[0]?.Id,
  };
};
